/**
 * Every drawn number, day and clock in this app, in one module.
 *
 * *Writing in the UI* says **a number is formatted, never written**, and for as
 * long as that rule existed it was held by nothing: `apps/mobile/src/lib/
 * entry-format.ts` did it correctly for the entry surfaces, and eight other
 * files reached for `Intl` themselves. The drift that produced is not
 * duplication — it is disagreement:
 *
 * - `account-model.ts` formatted a join date with `toLocaleDateString(undefined,
 *   …)`. `undefined` is the **device's** locale, not the app's, so a shopkeeper
 *   whose phone is set to another locale saw one date in this app written two
 *   ways. It was also the only `month: 'long'` anywhere.
 * - `business-present.ts` wrote the date-only/instant split by hand and its own
 *   docblock admitted it: "the same split `entry-format.ts` makes".
 * - Two files spelled a weekday, two spelled a day, one spelled a clock.
 *
 * **Why `packages/shared` and not `apps/mobile`.** `entry-format.ts` imports
 * `t()` and reads a record schema, so it cannot leave the app — but
 * `packages/ui/src/fields/money-calculator.logic.ts` needs to draw money and
 * cannot import from `apps/mobile` at all, so it had its own `Intl` call and no
 * way not to. Putting the vocabulary here is what lets the rule be ABSOLUTE:
 * with the seam below both trees, no file in either may spell a locale
 * formatter, so the guard needs no exemption list.
 *
 * `entry-format.ts` re-exports all of this, so its ~45 importers are untouched.
 *
 * **Indian digit grouping is not a preference.** 1,42,500 is how the launch
 * market writes a number, and a shopkeeper checking a total against a paper
 * ledger compares digit groups before they compare digits. The currency comes
 * from the record; a record without one is not money.
 *
 * **A month's abbreviation is the platform's and the two disagree** — `Sept` on
 * Android, `Sep` on iOS, for the same `en-IN`. It is each platform's own CLDR
 * data and no option settles it; see *Writing in the UI*, which states it as a
 * limit rather than papering over it. So a test may assert the SHAPE of a date
 * here and never its month.
 */

/** The locale a number is grouped in. INR groups the Indian way; nothing else does. */
const localeFor = (currency: string | null): string => (currency === 'INR' ? 'en-IN' : 'en-US');

/** Everything that is not money is still grouped the launch market's way. */
const LOCALE = 'en-IN';

/**
 * A stored day is a day, and a stored instant is an instant.
 *
 * `date` arrives as YYYY-MM-DD and means the same day everywhere, so it is read
 * in UTC. `datetime` arrives as a full instant and is read in the device's own
 * zone, which is the shop's zone.
 */
const asInstant = (stored: string): Date =>
  new Date(stored.length === 10 ? `${stored}T00:00:00.000Z` : stored);

/** True when a stored value is a bare day rather than an instant. */
const isDateOnly = (stored: string): boolean => stored.length === 10;

/** UTC for a stored day, the device's zone for a stored instant. */
const zoneFor = (stored: string): { timeZone?: 'UTC' } =>
  isDateOnly(stored) ? { timeZone: 'UTC' } : {};

/**
 * The amount, as a person reads it.
 *
 * Indian digit grouping when the currency is INR, because that is what the
 * launch market writes and 1,42,500 is not a formatting preference there.
 */
export const formatMoney = (value: number | null, currency: string | null): string => {
  if (value === null || !Number.isFinite(value)) return '';
  if (currency === null) return formatNumber(value);
  return new Intl.NumberFormat(localeFor(currency), {
    style: 'currency',
    currency,
    maximumFractionDigits: Number.isInteger(value) ? 0 : 2,
  }).format(value);
};

/** A plain number: a quantity, a count. No currency, same grouping rules. */
export const formatNumber = (value: number | null): string =>
  value === null || !Number.isFinite(value) ? '' : new Intl.NumberFormat(LOCALE).format(value);

/** A day inside a row: 5 Sep. The year belongs to the day heading, not a cell. */
/**
 * The month abbreviations, ours rather than the platform's.
 *
 * **`LOCALE` is pinned and the two platforms still disagree.** iOS writes
 * "9 Sep 2026" on the Records list and Android writes "9 Sept 2026", from the
 * same `toLocaleDateString('en-IN', { month: 'short' })` call — because the
 * abbreviation lives in each platform's own ICU data, and Android's is newer:
 * CLDR 42 lengthened September's English abbreviation to "Sept". A locale pins
 * the language, not the data behind it, so there is no option to `Intl` that
 * fixes this.
 *
 * *Writing in the UI* says a month's abbreviation is the platform's. It is the
 * one rule this file is allowed to overrule, and only because the platforms do
 * not agree with each other: a ledger is scanned down a column of dates, and a
 * column that reads Sept on one phone and Sep on another is two ledgers.
 *
 * `date-picker.logic.ts` wrote this same table by hand for its accessible
 * label. One table, here, and it imports it.
 */
export const MONTH_SHORT = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
] as const;

/**
 * The parts of a day, with the month ours and every digit still `Intl`'s.
 *
 * The month INDEX is read through `Intl` too, at `month: 'numeric'`, so it is
 * resolved in the same zone as the day and the year beside it. `getMonth()`
 * would be the local month, which is wrong for a stored date-only value that
 * `zoneFor` reads in UTC — and wrong only for a few hours a day, which is the
 * kind of bug that reaches a device.
 */
const dayParts = (
  instant: Date,
  zone: { timeZone?: 'UTC' },
): { readonly weekday: string; readonly day: string; readonly month: string; readonly year: string } => {
  const at = Number(instant.toLocaleDateString(LOCALE, { month: 'numeric', ...zone })) - 1;
  return {
    weekday: instant.toLocaleDateString(LOCALE, { weekday: 'short', ...zone }),
    day: instant.toLocaleDateString(LOCALE, { day: 'numeric', ...zone }),
    month: MONTH_SHORT[at] ?? '',
    year: instant.toLocaleDateString(LOCALE, { year: 'numeric', ...zone }),
  };
};

export const formatDayShort = (stored: string): string => {
  const instant = asInstant(stored);
  if (Number.isNaN(instant.getTime())) return '';
  const { day, month } = dayParts(instant, zoneFor(stored));
  return `${day} ${month}`;
};

/** A day with its year: 5 Sep 2026. What a heading and a detail row carry. */
export const formatDay = (stored: string): string => {
  const instant = asInstant(stored);
  if (Number.isNaN(instant.getTime())) return '';
  const { day, month, year } = dayParts(instant, zoneFor(stored));
  return `${day} ${month} ${year}`;
};

/**
 * A day with the month spelled out: 5 September 2026.
 *
 * The one long form, and it exists for exactly one row — "Member since" on
 * Your settings, where the date is read once and never scanned against others.
 * It used to be the app's only `toLocaleDateString(undefined, …)`, so it was
 * also the one date the app drew in the *device's* locale rather than its own.
 */
export const formatDayLong = (stored: string): string => {
  const instant = asInstant(stored);
  if (Number.isNaN(instant.getTime())) return '';
  return instant.toLocaleDateString(LOCALE, {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    ...zoneFor(stored),
  });
};

/** A weekday on a bar: Mon. Read in UTC, as a stored day was written. */
export const formatWeekday = (day: string): string => {
  const instant = asInstant(day);
  if (Number.isNaN(instant.getTime())) return '';
  return instant.toLocaleDateString(LOCALE, { weekday: 'short', ...zoneFor(day) });
};

/** A day on a card: Mon 24 Aug. The weekday earns its place when the bar has none. */
export const formatWeekdayDay = (day: string): string => {
  const instant = asInstant(day);
  if (Number.isNaN(instant.getTime())) return '';
  // "Mon, 24 Aug". The comma is the one `en-IN` put here, written down now that
  // the string is composed rather than asked for whole.
  const parts = dayParts(instant, zoneFor(day));
  return `${parts.weekday}, ${parts.day} ${parts.month}`;
};

/** A clock time a person reads: 6:40 pm. Lower-case — a row of capitals shouts. */
export const formatClock = (stored: string): string => {
  const instant = new Date(stored);
  if (Number.isNaN(instant.getTime())) return '';
  return instant
    .toLocaleTimeString(LOCALE, { hour: 'numeric', minute: '2-digit', hour12: true })
    .toLowerCase();
};

/** Both, for a record whose time axis carries a clock. */
export const formatDateTime = (stored: string): string => {
  const day = formatDayShort(stored);
  const clock = formatClock(stored);
  return day === '' ? '' : clock === '' ? day : `${day}, ${clock}`;
};

/**
 * A day, or a clock when it is today.
 *
 * What a conversation row shows: the time if it happened today, the day if it
 * did not — and the year only when it is not this one, because a year every
 * reader already knows is noise on every row.
 */
export const formatStamp = (stored: string, now: Date): string => {
  const instant = new Date(stored);
  if (Number.isNaN(instant.getTime())) return '';
  const sameDay =
    instant.getFullYear() === now.getFullYear() &&
    instant.getMonth() === now.getMonth() &&
    instant.getDate() === now.getDate();
  if (sameDay) return formatClock(stored);
  const { day, month, year } = dayParts(instant, {});
  return instant.getFullYear() === now.getFullYear()
    ? `${day} ${month}`
    : `${day} ${month} ${year}`;
};

/**
 * A date range, said the way the entry filter bar says one.
 *
 * Four cases, and each drops what the other end already establishes: one day on
 * its own, a stretch inside one month, a stretch across months of one year, and
 * two full dates when even the year changes.
 */
export const formatDayRange = (fromDay: string, toDay: string): string => {
  const from = asInstant(fromDay.slice(0, 10));
  const to = asInstant(toDay.slice(0, 10));
  if (Number.isNaN(from.getTime()) || Number.isNaN(to.getTime())) return '';
  const part = (instant: Date): { day: string; month: string; year: string } =>
    dayParts(instant, { timeZone: 'UTC' });

  const full = (instant: Date): string => {
    const { day, month, year } = part(instant);
    return `${day} ${month} ${year}`;
  };
  const sameYear = from.getUTCFullYear() === to.getUTCFullYear();
  const sameMonth = sameYear && from.getUTCMonth() === to.getUTCMonth();
  if (sameMonth && from.getUTCDate() === to.getUTCDate()) return full(from);
  if (sameMonth) return `${part(from).day}–${full(to)}`;
  if (sameYear) return `${part(from).day} ${part(from).month} – ${full(to)}`;
  return `${full(from)} – ${full(to)}`;
};

/**
 * The symbol a currency is written with, for the hero to put beside the number.
 *
 * The amount box is a native view that takes a string of digits and knows
 * nothing about money, so the symbol is drawn in JavaScript beside it. `Intl`
 * is what already formats every amount the app shows (`formatMoney` in the app
 * layer), so it is what decides here too rather than a hand-kept map that would
 * drift the first time a business picks a currency nobody thought of.
 *
 * The code itself is the fallback. A runtime without `formatToParts`, or a
 * currency `Intl` refuses, then shows "INR 1,140" instead of nothing at all —
 * wrong-looking beats a number with no unit on it.
 */
export function symbolFor(currency: string | null | undefined): string | null {
  if (currency === null || currency === undefined || currency.trim() === '') {
    return null;
  }
  const code = currency.trim().toUpperCase();
  try {
    // `format`, not `formatToParts`. Hermes ships an `Intl` that formats but
    // whose `formatToParts` answered no currency part on the device, so the box
    // read "INR 0" where it should have read the rupee sign. `format` is what
    // the app layer already trusts for every amount it prints.
    const printed = new Intl.NumberFormat(code === 'INR' ? 'en-IN' : 'en-US', {
      style: 'currency',
      currency: code,
      maximumFractionDigits: 0,
    }).format(0);
    // Everything that is not the symbol: the digits, the grouping, the space a
    // few locales put between the two, and the non-breaking space some use.
    const symbol = printed.replace(/[0-9\s\u00A0.,\u2009\u202F-]/g, '');
    return symbol === '' ? code : symbol;
  } catch {
    return code;
  }
}

/** How a currency groups its digits. Read once per field; hand to the native box. */
export type DigitGrouping = Readonly<{
  groupSeparator: string;
  decimalSeparator: string;
  /** 0 means the locale draws no separator at all. */
  primaryGroupSize: number;
  secondaryGroupSize: number;
}>;

/** One probe per locale rather than one per field. Keyed by what `localeFor` answered. */
const GROUPINGS = new Map<string, DigitGrouping>();

/**
 * The four facts, read off one number this locale printed.
 *
 * 1234567.5 is the smallest number that shows all four at once: two group
 * boundaries, so a locale whose groups are not all one size says so on the
 * string, and a fraction, so the decimal mark is on it too.
 */
const probeGrouping = (locale: string): DigitGrouping => {
  const printed = new Intl.NumberFormat(locale).format(1234567.5);
  // Everything that is not a digit, in the order it was printed. The LAST of
  // them is the decimal mark, because nothing follows the fraction; the FIRST
  // is the group mark, unless the two are the same character, which is a
  // locale saying it printed no group mark at all.
  const marks = printed.replace(/[0-9]/g, '');
  const decimalSeparator = marks.slice(-1) === '' ? '.' : marks.slice(-1);
  const groupSeparator = marks.slice(0, 1);
  if (groupSeparator === '' || groupSeparator === decimalSeparator) {
    // Nothing to read. A size of 0 means "do not group", so the character
    // handed back is never drawn and is the native box's own default.
    return { groupSeparator: ',', decimalSeparator, primaryGroupSize: 0, secondaryGroupSize: 0 };
  }
  // The integer part, cut in the groups this locale chose. `en-IN` prints
  // 12,34,567 and `en-US` prints 1,234,567, so the last group is the one a
  // number always has and the one before it is the one that repeats.
  const groups = printed.slice(0, printed.lastIndexOf(decimalSeparator)).split(groupSeparator);
  const primaryGroupSize = (groups.at(-1) ?? '').length;
  return {
    groupSeparator,
    decimalSeparator,
    primaryGroupSize,
    secondaryGroupSize: groups.length < 2 ? primaryGroupSize : (groups.at(-2) ?? '').length,
  };
};

/**
 * The facts for a currency, from the same locale `formatMoney` groups in. Memoised per locale.
 *
 * **The box and the list must not be able to disagree.** A person types
 * `10000` on the keypad and the native amount box draws the buffer — the raw
 * string the engine parses, with nothing between it and the glyphs that knows
 * what a comma is — while the list row beside it says ₹10,000. The box cannot
 * call `formatMoney`, because what is on it is a half-typed expression and not
 * a number. So it takes the RULE instead: `localeFor` above, the one line that
 * decides Indian grouping from western, and these four facts read off it.
 *
 * **Nothing here is a hand-kept table.** `Intl` is asked the same question the
 * list asks, so the day `localeFor` grows a third locale the box follows it
 * without being edited. `format` and not `formatToParts`, for the reason
 * `symbolFor` above records: Hermes ships an `Intl` that formats but whose
 * `formatToParts` answered no part on the device.
 */
export const digitGroupingFor = (currency: string | null): DigitGrouping => {
  const locale = localeFor(currency);
  const known = GROUPINGS.get(locale);
  if (known !== undefined) return known;
  const facts = probeGrouping(locale);
  GROUPINGS.set(locale, facts);
  return facts;
};

/** The digits before the point, cut from the right. See `groupDigits`. */
const groupInteger = (digits: string, grouping: DigitGrouping): string => {
  const { groupSeparator, primaryGroupSize, secondaryGroupSize } = grouping;
  if (primaryGroupSize <= 0 || digits.length <= primaryGroupSize) return digits;
  // A secondary of 0 beside a primary that groups would never end, so it falls
  // back to the primary. `primaryGroupSize: 0` is how a locale says "no
  // grouping", and that is already answered above.
  const step = secondaryGroupSize <= 0 ? primaryGroupSize : secondaryGroupSize;
  const parts = [digits.slice(digits.length - primaryGroupSize)];
  let rest = digits.slice(0, digits.length - primaryGroupSize);
  while (rest.length > step) {
    parts.unshift(rest.slice(rest.length - step));
    rest = rest.slice(0, rest.length - step);
  }
  parts.unshift(rest);
  return parts.join(groupSeparator);
};

/**
 * A buffer or a result, as the box draws it. Pure. The buffer is never changed.
 *
 * **A numeric run is grouped and everything else passes through.** A run is a
 * maximal sequence of `0-9` and `.`; the characters before its first `.` are
 * grouped from the right, the first `.` becomes the locale's decimal mark, and
 * the digits after it are left exactly as they were typed. An operator, a `%`,
 * a leading `-` and a second `.` are none of them digits, so none of them are
 * touched: `12000+3500` draws 12,000+3,500, `1000.` draws 1,000. and `200+10%`
 * draws 200+10%.
 *
 * It is applied on the way to the glyphs and nowhere else. A separator is never
 * a key, never in the buffer, and never reaches the engine — which goes on
 * parsing `12000+3500` while the screen says 12,000+3,500.
 */
export const groupDigits = (text: string, grouping: DigitGrouping): string =>
  text.replace(/[0-9.]+/g, (run) => {
    const point = run.indexOf('.');
    if (point < 0) return groupInteger(run, grouping);
    const integer = groupInteger(run.slice(0, point), grouping);
    return `${integer}${grouping.decimalSeparator}${run.slice(point + 1)}`;
  });

/** The part of the day a greeting names (S12). */
export type TimeOfDay = 'morning' | 'afternoon' | 'evening';

/**
 * The part of the day a greeting names, in the call's zone (S12): morning
 * before 12:00, afternoon before 17:00, evening after that. An empty or absent
 * zone reads UTC, and a zone `Intl` refuses reads the UTC hour, as the
 * server's `zoneDay` does for the date.
 */
export const timeOfDay = (zone: string | undefined, now: Date): TimeOfDay => {
  const timeZone = zone === undefined || zone.trim() === '' ? 'UTC' : zone.trim();
  let hour: number;
  try {
    hour = Number(new Intl.DateTimeFormat('en-GB', { timeZone, hour: '2-digit', hourCycle: 'h23' }).format(now));
  } catch {
    // Intl throws RangeError on a zone it does not know.
    hour = now.getUTCHours();
  }
  if (hour < 12) return 'morning';
  if (hour < 17) return 'afternoon';
  return 'evening';
};
